import { QUERY_KEYS } from '~/constants/react-query'
import { REFETCH_ORG_GAS } from '~/constants/refetch-intervals'
import { useActiveOrg } from '~/hooks/org/useActiveOrg'
import { PlanName } from '~/hooks/usePlans'
import { useQueryWrapper } from '~/hooks/useQueryWrapper'
import { useServerRequests } from '~/hooks/useRequests'

// Sponsored gas (plan budget plus gas balance) under $5 or at zero. The
// server owns the thresholds.
export type GasAlertKind = 'low' | 'empty'

export type OrgGas = {
  plan: {
    name: PlanName
    /** Which source pays first: the plan's budget, a custom policy, or nothing is metered yet */
    pot: 'plan' | 'custom_policy' | 'unmetered'
    remainingBudgetUsd: number | null
    windowEnd: string | null
  }
  gasBalance: {
    balanceUsd: number
  }
  alert: GasAlertKind | null
}

export const useOrgGas = () => {
  const { org, featureFlags } = useActiveOrg()
  const activeOrgId = org?.id
  const { serverGetRequest } = useServerRequests()
  const enabled = !!activeOrgId && !!featureFlags?.['gas-account']

  const { data, dataUpdatedAt, isLoading } = useQueryWrapper<OrgGas>({
    queryKey: [QUERY_KEYS.ORG_GAS, activeOrgId],
    enabled,
    refetchInterval: REFETCH_ORG_GAS,
    queryFn: async () => {
      const response = await serverGetRequest<OrgGas>({
        path: '/api/org/gas',
        params: { orgId: activeOrgId ?? '' },
      })
      if (!response) throw new Error('Failed to fetch gas balance')
      return response.data
    },
  })

  return {
    plan: data?.plan,
    gasBalance: data?.gasBalance,
    // Gated again so a cached alert does not outlive the flag.
    alert: enabled ? (data?.alert ?? null) : null,
    // When the balance was last read; the banner's clock, so render stays pure.
    fetchedAt: dataUpdatedAt,
    isLoading,
  }
}
